import { gql } from '@apollo/client';
import * as React from 'react';
import { ShopLocationPicker } from 'src/Components/ShopLocationPicker/ShopLocationPicker';
import { Button } from 'src/shadcn/components/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shadcn/components/ui/dialog';
import { useHotToast } from 'src/utils/useHotToast';
import {
  useCreateNewJobMutation,
} from './__generated__/CreateNewJobConfirmationModalBody.generated';
import { RouterLinks } from 'src/routing/routerLinks';
import * as uuid from 'uuid';
import { useAsmRouter } from 'src/utils/hooks';
import { useAuthContext } from 'src/Components/AuthContext';
import { WithoutTypename } from 'src/utils/utils';
import { RotateCw } from 'lucide-react';
import { GetShopLocationsForContextQuery } from 'src/Components/Pages/__generated__/PageWithUserContext.generated';
import { useShopLocationsContext } from 'src/Components/WorkOrdersPage/Contexts/ShopLocationsContext';
import { ListJobsForTableDocument } from 'src/Components/WorkOrdersPage/__generated__/WorkOrdersPage.generated';
function useDefaultShopLocation() {
  const authContext = useAuthContext();
  const shopLocationContext = useShopLocationsContext();
  if (authContext.user?.shopLocation) {
    return authContext.user.shopLocation;
  }
  if (shopLocationContext.shopLocations.length === 1) {
    return shopLocationContext.shopLocations[0];
  }
  return undefined;
}
export interface ICreateNewJobConfirmationModalBodyProps {}

export const CreateNewJobConfirmationModalBody: React.FunctionComponent<
  ICreateNewJobConfirmationModalBodyProps
> = props => {
  const toast = useHotToast();
  const defaultShopLocation = useDefaultShopLocation();
  const [selectedLocation, setSelectedLocation] = React.useState<
    | WithoutTypename<
        GetShopLocationsForContextQuery['getShopLocations'][number]
      >
    | undefined
  >(defaultShopLocation);

  const { isSavingJob, saveJob } = useCreateNewJob();
  return (
    <>
      <DialogHeader>
        <DialogTitle>Create new work order?</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      {!defaultShopLocation && (
        <ShopLocationPicker
          selectedLocation={selectedLocation}
          onLocationPicked={setSelectedLocation}
        />
      )}
      <DialogFooter>
        <Button
          variant={'outline'}
          disabled={isSavingJob}
          onClick={() => {
            if (selectedLocation) {
              saveJob(selectedLocation.id, undefined);
            } else {
              toast.error('Please select a location');
            }
          }}
        >
          <span>Create a work order without appointment</span>
          {isSavingJob && <RotateCw className='ml-2 size-4 animate-spin' />}
        </Button>
      </DialogFooter>
    </>
  );
};

const useCreateNewJob = () => {
  const router = useAsmRouter();
  const toast = useHotToast();
  const [setJob, { loading: isSavingJob }] = useCreateNewJobMutation({
    fetchPolicy: 'no-cache',
  });

  return {
    saveJob: (shopLocationId: string, appointmentId: string | undefined) => {
      const toastId = toast.loading('Creating new work order');
      const newJobId = uuid.v4();
      setJob({
        variables: { jobInput: { id: newJobId, shopLocationId } },
        onCompleted: j => {
          router.pushFlat(RouterLinks.workOrders, [newJobId]);
          toast.dismiss(toastId);
        },
        onError: err => {
          toast.error(err.message, { id: toastId });
        },
        refetchQueries: [ListJobsForTableDocument],
      });
    },
    isSavingJob,
  };
};

export const CREATE_NEW_JOB = gql`
  mutation CreateNewJob($jobInput: CreateJobInput!) {
    createNewJob(jobInput: $jobInput) {
      throwAway
    }
  }
`;
